import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Modal,
  Button,
  Badge,
  Card,
  Table,
  Row,
  Col,
  Form,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import BASE_URL from '../config';
import './BranchManager.css';
import './PaymentPortal.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  FaEye,
  FaPaperclip,
  FaExternalLinkAlt,
  FaSearch,
  FaSignOutAlt,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import OrderTracking from './OrderTracking';

// --- START OF SHARED UTILITY FUNCTIONS ---

/**
 * Helper function to clean the BASE_URL to the domain root for serving static files.
 */
const getStorageBaseUrl = () => {
  try {
    // Parse the full API URL (e.g., http://domain:8000/api)
    const url = new URL(BASE_URL);

    // Construct the base URL without the /api path segment
    let root = `${url.protocol}//${url.hostname}`;

    // If the port is explicitly defined and NOT standard HTTP/S, include it
    if (url.port && url.port !== '80' && url.port !== '443') {
      root += `:${url.port}`;
    }

    // Remove trailing slash if it exists
    if (root.endsWith('/')) {
      root = root.slice(0, -1);
    }

    return root;
  } catch (e) {
    console.error('Error parsing BASE_URL for static files:', e);
    return BASE_URL.replace(/\/api$/, '');
  }
};

/**
 * Helper function to format DateTime into DD-MM-YYYY, HH:MM:SS AM/PM format.
 * NOTE: Not strictly needed here but useful for consistency if added to table.
 */
const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  try {
    const date = new Date(dateTimeString);

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    return `${formattedDate}, ${time}`;
  } catch (e) {
    return 'Invalid Date';
  }
};

// --- END OF SHARED UTILITY FUNCTIONS ---

// Axios instance with Bearer token
const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const BranchManager = () => {
  const navigate = useNavigate();

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/');
  };

  const [activeForm, setActiveForm] = useState('formA');
  const [orders, setOrders] = useState([]);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState({
    pending_transfers: 0,
    unassigned_orders: 0,
    active_drivers: 0,
  });
  const [allDrivers, setAllDrivers] = useState([]);
  const [showroomDrivers, setShowroomDrivers] = useState([]); // Drivers filtered by showroom
  const [userInfo, setUserInfo] = useState({ name: '', branches: [] });

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackOrder, setTrackOrder] = useState(null);
  const [showTransferViewModal, setShowTransferViewModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [viewOrderAttachments, setViewOrderAttachments] = useState({
    pod_photos: [],
    voice_notes: [],
    order_docs: [],
  });

  // Remove dropdown filter – use showroom from login
  const [selectedShowroomId, setSelectedShowroomId] = useState('');

  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filteredTransfers, setFilteredTransfers] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [driverSummaries, setDriverSummaries] = useState([]);
  const [driverLoadDateFilter, setDriverLoadDateFilter] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split('T')[0],
    to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
      .toISOString()
      .split('T')[0],
  });

  const handleCloseAssign = () => setShowAssignModal(false);
  const handleShowAssign = (order) => {
    setSelectedOrder(order);
    setSelectedDriverId('');
    setShowAssignModal(true);
  };
  const handleCloseView = () => setShowViewModal(false);
  const handleTrackDriver = (order) => {
    setTrackOrder(order);
    setShowTrackModal(true);
  };
  const handleCloseTransferView = () => setShowTransferViewModal(false);
  const handleShowTransferView = async (transfer) => {
    setSelectedTransfer(transfer);
    try {
      // Fetch full transfer details with all embedded data
      const res = await authAxios.get(
        `/transfers/${transfer.id}?embed=order,from_driver,to_driver,from_showroom,to_showroom,original_order,transfer_order`
      );
      const transferDetails = res.data.data || res.data;
      setSelectedTransfer(transferDetails);
    } catch (err) {
      console.error('Error fetching transfer details:', err);
    }
    setShowTransferViewModal(true);
  };
  const handleCloseTrackModal = () => {
    setShowTrackModal(false);
    setTrackOrder(null);
  };

  // ✅ Get user information and branches from API (similar to PaymentPortal)
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // Get user ID from localStorage
        const userId = localStorage.getItem('userId');
        if (!userId) {
          // Fallback to localStorage if no userId
          const userName = localStorage.getItem('userName') || 'Unknown User';
          const branchName =
            localStorage.getItem('branchName') || 'Unknown Branch';
          setUserInfo({
            name: userName,
            branches: branchName !== 'Unknown Branch' ? [branchName] : [],
          });
          return;
        }

        // Fetch user details from API
        const res = await authAxios.get(`/users/${userId}`);
        const userData = res.data?.data || res.data || {};

        // Extract showrooms/branches
        const userBranches = Array.isArray(userData.showrooms)
          ? userData.showrooms.map((s) => s.name || s)
          : [];

        setUserInfo({
          name:
            userData.name || localStorage.getItem('userName') || 'Unknown User',
          branches: userBranches,
        });
      } catch (err) {
        console.error('Failed to fetch user info:', err);
        // Fallback to localStorage
        const userName = localStorage.getItem('userName') || 'Unknown User';
        const branchName =
          localStorage.getItem('branchName') || 'Unknown Branch';
        setUserInfo({
          name: userName,
          branches: branchName !== 'Unknown Branch' ? [branchName] : [],
        });
      }
    };

    fetchUserInfo();
  }, []);

  // Function to handle opening attachments (FIXED)
  const handleOpenAttachment = (file) => {
    if (!file.path) {
      alert('File path missing. Cannot display file.');
      return;
    }

    const storageRoot = getStorageBaseUrl();
    // Ensure path includes 'storage/' but only once
    const publicStoragePath = file.path.startsWith('storage/')
      ? file.path
      : `storage/${file.path}`;

    const fileUrl = `${storageRoot}/${publicStoragePath}`; // FIXED URL

    window.open(fileUrl, '_blank');
  };

  const handleShowView = async (order) => {
    setSelectedOrder(order);
    try {
      // NOTE: The API response structure often differs slightly between list and single-item endpoints.
      const res = await authAxios.get(
        `/orders/${order.id}?embed=attachments,customer,vendor,driver,showroom,warehouse`
      );
      const orderWithAttachments = res.data.data || res.data;

      // Update selectedOrder with the full order details from the API
      // This ensures all fields like pickup_address are available
      setSelectedOrder(orderWithAttachments);

      // Function to generate corrected attachment URL
      const getAttachmentUrl = (path) => {
        const storageRoot = getStorageBaseUrl();
        const publicStoragePath = path.startsWith('storage/')
          ? path
          : `storage/${path}`;
        return `${storageRoot}/${publicStoragePath}`;
      };

      // Process attachments (FIXED: using getAttachmentUrl)
      const attachments = Array.isArray(
        orderWithAttachments.attachments?.data
          ? orderWithAttachments.attachments.data
          : orderWithAttachments.attachments
      )
        ? orderWithAttachments.attachments?.data ||
          orderWithAttachments.attachments
        : [];

      const pod_photos = attachments
        .filter((a) => a.type?.toLowerCase() === 'pod_photo')
        .map((a) => ({ ...a, url: a.path ? getAttachmentUrl(a.path) : null }));

      const voice_notes = attachments
        .filter((a) => a.type?.toLowerCase() === 'voice_note')
        .map((a) => ({ ...a, url: a.path ? getAttachmentUrl(a.path) : null }));

      const order_docs = attachments
        .filter((a) => a.type?.toLowerCase() === 'order_document')
        .map((a) => ({ ...a, url: a.path ? getAttachmentUrl(a.path) : null }));

      setViewOrderAttachments({
        pod_photos,
        voice_notes,
        order_docs,
      });
    } catch (err) {
      console.error('Error fetching order details:', err);
      setViewOrderAttachments({
        pod_photos: [],
        voice_notes: [],
        order_docs: [],
      });
    }
    setShowViewModal(true);
  };

  // Fetch pending orders
  const fetchPendingOrders = useCallback(async () => {
    try {
      let page = 1;
      let allPendingOrders = [];
      let hasNextPage = true;

      while (hasNextPage) {
        const res = await authAxios.get(
          `/orders?page=${page}&status=in_transit&embed=customer,vendor,driver,showroom,warehouse`
        );
        const pageOrders = res.data?.data?.data || res.data?.data || [];
        allPendingOrders = [...allPendingOrders, ...pageOrders];

        const meta = res.data?.data?.meta || res.data?.meta;
        hasNextPage = meta && meta.current_page < meta.last_page;
        page++;
      }

      setPendingOrders(allPendingOrders);
    } catch (err) {
      console.error('Error fetching pending orders:', err);
      setPendingOrders([]);
    }
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Orders - include showroom and warehouse in embed
        let ordersPage = 1;
        let allOrders = []; // Declare allOrders here
        let hasNextOrderPage = true;
        while (hasNextOrderPage) {
          const res = await authAxios.get(
            `/orders?embed=customer,vendor,driver,showroom,warehouse&page=${ordersPage}`
          );
          const pageOrders = res.data?.data?.data || [];
          allOrders = [...allOrders, ...pageOrders];
          const meta = res.data?.data?.meta;
          hasNextOrderPage = meta && meta.current_page < meta.last_page;
          ordersPage++;
        }
        setOrders(allOrders);

        // Fetch unassigned orders with status=pending
        let unassignedPage = 1;
        let allUnassignedOrders = [];
        let hasNextUnassignedPage = true;
        while (hasNextUnassignedPage) {
          const unassignedRes = await authAxios.get('/orders', {
            params: {
              embed: 'customer,vendor,driver,showroom,warehouse',
              status: 'pending',
              page: unassignedPage,
            },
          });
          const pageUnassignedOrders = unassignedRes.data?.data?.data || [];
          allUnassignedOrders = [
            ...allUnassignedOrders,
            ...pageUnassignedOrders,
          ];
          const unassignedMeta = unassignedRes.data?.data?.meta;
          hasNextUnassignedPage =
            unassignedMeta &&
            unassignedMeta.current_page < unassignedMeta.last_page;
          unassignedPage++;
        }
        setUnassignedOrders(allUnassignedOrders);

        // Transfers - fetch with embedded data
        try {
          const transfersRes = await authAxios.get(
            `/transfers?embed=order,from_driver,to_driver,from_showroom,to_showroom`
          );
          console.log('📦 Raw Transfers Response:', transfersRes.data);
          // Handle paginated response structure: {success: true, data: {data: [...]}}
          let transfersData = [];
          if (transfersRes.data?.success && transfersRes.data?.data?.data) {
            transfersData = transfersRes.data.data.data;
          } else if (transfersRes.data?.data?.data) {
            transfersData = transfersRes.data.data.data;
          } else if (transfersRes.data?.data) {
            transfersData = Array.isArray(transfersRes.data.data)
              ? transfersRes.data.data
              : [];
          } else if (Array.isArray(transfersRes.data)) {
            transfersData = transfersRes.data;
          }
          console.log(
            '📦 Fetched Transfers:',
            transfersData.length,
            transfersData
          );
          setTransfers(transfersData);
        } catch (err) {
          console.error('❌ Error fetching transfers:', err);
          setTransfers([]);
        }

        // Summary
        const summaryRes = await authAxios.get(
          `/dashboard/branch-manager-summary`
        );
        const summaryData = summaryRes.data?.data || {
          unassigned_orders: 0,
          active_drivers: 0,
        };
        console.log('Summary API Response:', summaryData);

        // If summary includes showroom_id, set it
        if (summaryData.showroom_id) {
          setSelectedShowroomId(summaryData.showroom_id);
          console.log('Assigned showroom_id:', summaryData.showroom_id);
        }

        setSummary(summaryData);

        // Drivers - fetch all for driver summaries
        const driversRes = await authAxios.get(`/drivers`);
        setAllDrivers(driversRes.data?.data?.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response) {
          console.error('Response Data:', err.response.data);
          console.error('Response Status:', err.response.status);
        }
      }
    };

    fetchData();
    fetchPendingOrders();
  }, [fetchPendingOrders]);

  // Fetch drivers for the selected showroom
  useEffect(() => {
    const fetchShowroomDrivers = async () => {
      if (!selectedShowroomId) {
        setShowroomDrivers([]);
        return;
      }

      try {
        // Fetch drivers filtered by showroom_id
        const driversRes = await authAxios.get(`/drivers`, {
          params: { showroom_id: selectedShowroomId },
        });
        const driversData =
          driversRes.data?.data?.data || driversRes.data?.data || [];
        setShowroomDrivers(Array.isArray(driversData) ? driversData : []);
      } catch (err) {
        console.error('Error fetching showroom drivers:', err);
        setShowroomDrivers([]);
      }
    };

    fetchShowroomDrivers();
  }, [selectedShowroomId]);

  // Auto-filter orders and transfers based on logged in showroom
  useEffect(() => {
    if (!selectedShowroomId) {
      // If no showroom selected, show all transfers
      setFilteredTransfers(transfers);
      setFilteredOrders(orders);
      return;
    }

    let newFilteredOrders = orders.filter(
      (order) =>
        String(order.showroom_id) === String(selectedShowroomId) &&
        !order.is_transfer &&
        order.is_transfer !== 1 &&
        order.is_transfer !== '1'
    );

    // Filter transfers: show if from_showroom_id or to_showroom_id matches, or if original_order/transfer_order showroom matches
    let newFilteredTransfers = transfers.filter((t) => {
      if (!t) return false;

      const showroomIdStr = String(selectedShowroomId);

      // Check direct showroom IDs
      const fromShowroomMatch =
        (t.from_showroom_id && String(t.from_showroom_id) === showroomIdStr) ||
        (t.original_order?.showroom_id &&
          String(t.original_order.showroom_id) === showroomIdStr);

      const toShowroomMatch =
        (t.to_showroom_id && String(t.to_showroom_id) === showroomIdStr) ||
        (t.transfer_order?.showroom_id &&
          String(t.transfer_order.showroom_id) === showroomIdStr);

      return fromShowroomMatch || toShowroomMatch;
    });

    console.debug('📊 Filtering data for showroom:', selectedShowroomId);
    console.debug('➡️ Total Transfers:', transfers.length);
    console.debug('➡️ Filtered Transfers:', newFilteredTransfers.length);
    console.debug('➡️ Sample Transfer:', transfers[0]);
    console.debug('➡️ Filtered Orders:', newFilteredOrders.length);

    setFilteredOrders(newFilteredOrders);
    setFilteredTransfers(newFilteredTransfers);
  }, [selectedShowroomId, orders, transfers]);

  // Helper functions to get names based on order type
  const getVendorName = (order) => order?.vendor?.name || 'Unknown Vendor';

  const getDestinationName = (order) => {
    if (order.type === 'purchase_order') {
      return (
        order.showroom?.name || order.warehouse?.name || 'Unknown Destination'
      );
    } else {
      return order.customer?.name || 'Unknown Customer';
    }
  };

  const getDriverName = (order) => order?.driver?.name || 'Unassigned';

  const driverOptions = Array.isArray(showroomDrivers) ? showroomDrivers : [];

  // Fetch driver summary list for driver load
  const fetchDriverSummaries = useCallback(async () => {
    try {
      // Only use the current logged-in branch manager's showroom ID
      if (!selectedShowroomId) {
        setDriverSummaries([]);
        return;
      }

      // Build URL with showroom_id parameter for current branch manager
      // Format: driver-summary-list?showroom_id=1&date_from=2025-11-01&date_to=2025-11-30
      const searchParams = new URLSearchParams();
      searchParams.append('date_from', driverLoadDateFilter.from);
      searchParams.append('date_to', driverLoadDateFilter.to);

      // Handle pagination - fetch all pages
      let page = 1;
      let allSummaries = [];
      let hasNextPage = true;

      while (hasNextPage) {
        const pageParams = new URLSearchParams(searchParams);
        pageParams.append('page', page);

        const res = await authAxios.get(
          `/driver-summary-list?${pageParams.toString()}`
        );

        // Extract data from paginated response: res.data.data.data
        const pageData = res.data?.data?.data || res.data?.data || [];
        const pageSummaries = Array.isArray(pageData) ? pageData : [];
        allSummaries = [...allSummaries, ...pageSummaries];

        // Check if there are more pages
        const meta = res.data?.data;
        hasNextPage = meta && meta.current_page < meta.last_page;
        page++;
      }

      // Map the response to ensure consistent structure
      const mappedSummaries = allSummaries.map((summary) => ({
        driverId: summary.driver_id || summary.driverId || summary.id,
        driverName:
          summary.driver_name ||
          summary.driverName ||
          summary.name ||
          summary.driver?.name ||
          'Unknown Driver',
        confirmationPending: summary.confirmationPending || 0,
        completed: summary.completed || 0,
        collections: summary.collections || 0,
        dateFrom: summary.date_from || driverLoadDateFilter.from,
        dateTo: summary.date_to || driverLoadDateFilter.to,
      }));

      setDriverSummaries(mappedSummaries);
    } catch (err) {
      console.error('Error fetching driver summary list:', err);
      setDriverSummaries([]);
    }
  }, [selectedShowroomId, driverLoadDateFilter]);

  // Fetch driver summaries when showroom ID or date filter changes
  useEffect(() => {
    if (selectedShowroomId) {
      fetchDriverSummaries();
    } else {
      setDriverSummaries([]);
    }
  }, [selectedShowroomId, driverLoadDateFilter, fetchDriverSummaries]);

  const assignDriver = async () => {
    if (!selectedOrder || !selectedDriverId)
      return alert('Select a driver first');
    try {
      await authAxios.post(`/orders/${selectedOrder.id}/assign-driver`, {
        driver_id: selectedDriverId,
      });
      alert('Driver assigned successfully!');
      setShowAssignModal(false);

      const assignedDriver = driverOptions.find(
        (d) => String(d.id) === String(selectedDriverId)
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.id === selectedOrder.id
            ? { ...o, driver: assignedDriver, status: 'assigned' }
            : o
        )
      );

      // Refresh pending orders after assignment
      fetchPendingOrders();
    } catch (err) {
      console.error('Error assigning driver:', err);
      alert('Failed to assign driver');
    }
  };

  const updateTransferStatus = async (transferId, status) => {
    try {
      // Map status to action
      const action = status === 'approved' ? 'approve' : 'reject';
      // Set default remarks based on action
      const remarks =
        action === 'approve' ? 'Verified and approved' : 'Transfer rejected';

      // Use new API format: POST /transfers/{id}/status
      await authAxios.post(`/transfers/${transferId}/status`, {
        action,
        remarks,
      });
      alert(`Transfer ${status}!`);
      // Refresh transfers from API
      const transfersRes = await authAxios.get(
        `/transfers?embed=order,from_driver,to_driver,from_showroom,to_showroom`
      );
      // Handle paginated response structure: {success: true, data: {data: [...]}}
      let transfersData = [];
      if (transfersRes.data?.success && transfersRes.data?.data?.data) {
        transfersData = transfersRes.data.data.data;
      } else if (transfersRes.data?.data?.data) {
        transfersData = transfersRes.data.data.data;
      } else if (transfersRes.data?.data) {
        transfersData = Array.isArray(transfersRes.data.data)
          ? transfersRes.data.data
          : [];
      } else if (Array.isArray(transfersRes.data)) {
        transfersData = transfersRes.data;
      }
      setTransfers(transfersData);
    } catch (err) {
      console.error(`Error updating transfer ${transferId}:`, err);
      alert('Failed to update transfer');
    }
  };

  // Ensure body can scroll when BranchManager is mounted
  useEffect(() => {
    // Override AdminLayout.css which sets overflow: hidden
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyHeight = document.body.style.height;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlHeight = document.documentElement.style.height;
    const rootElement = document.getElementById('root');
    const originalRootOverflow = rootElement?.style.overflow;
    const originalRootHeight = rootElement?.style.height;

    // Force enable scrolling
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.maxHeight = 'none';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.maxHeight = 'none';

    if (rootElement) {
      rootElement.style.overflow = 'auto';
      rootElement.style.height = 'auto';
      rootElement.style.maxHeight = 'none';
    }

    return () => {
      // Reset on unmount
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.height = originalBodyHeight;
      document.body.style.maxHeight = '';
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.height = originalHtmlHeight;
      document.documentElement.style.maxHeight = '';
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow || '';
        rootElement.style.height = originalRootHeight || '';
        rootElement.style.maxHeight = '';
      }
    };
  }, []);

  return (
    <div
      style={{
        background: '#f0f5ff',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Abstract Background Shapes */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Large abstract shapes */}
        <div
          style={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '-100px',
            left: '-50px',
            animation: 'float 20s infinite ease-in-out',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(186, 230, 253, 0.5)',
            top: '20%',
            right: '-80px',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '3s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(224, 242, 254, 0.6)',
            bottom: '10%',
            left: '10%',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '6s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.3)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '9s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(186, 230, 253, 0.4)',
            bottom: '-50px',
            right: '20%',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '12s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'rgba(224, 242, 254, 0.5)',
            top: '70%',
            right: '10%',
            animation: 'float 20s infinite ease-in-out',
            animationDelay: '15s',
          }}
        ></div>

        {/* Medium dots */}
        <div
          style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(30, 64, 175, 0.5)',
            top: '12%',
            left: '30%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            background: 'rgba(37, 99, 235, 0.45)',
            top: '35%',
            left: '65%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '2s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '85px',
            height: '85px',
            borderRadius: '50%',
            background: 'rgba(59, 130, 246, 0.5)',
            top: '55%',
            left: '20%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '4s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '75px',
            height: '75px',
            borderRadius: '50%',
            background: 'rgba(96, 165, 250, 0.4)',
            top: '25%',
            left: '75%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '1s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '90px',
            height: '90px',
            borderRadius: '50%',
            background: 'rgba(30, 64, 175, 0.45)',
            top: '65%',
            left: '50%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '3s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '65px',
            height: '65px',
            borderRadius: '50%',
            background: 'rgba(37, 99, 235, 0.4)',
            top: '8%',
            left: '55%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(59, 130, 246, 0.45)',
            top: '45%',
            left: '10%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '1.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            background: 'rgba(96, 165, 250, 0.5)',
            top: '72%',
            left: '70%',
            animation: 'floatMediumDot 18s infinite ease-in-out',
            animationDelay: '3.5s',
          }}
        ></div>

        {/* Small dots */}
        <div
          style={{
            position: 'absolute',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '15%',
            left: '20%',
            animation: 'floatDot 15s infinite ease-in-out',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '25%',
            left: '60%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '1s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '35%',
            left: '15%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '2s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '45px',
            height: '45px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '45%',
            left: '70%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '0.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '55%',
            left: '30%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '1.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '65%',
            left: '80%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '2.5s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '20%',
            left: '45%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '3s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '30%',
            left: '10%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '0.8s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '40%',
            left: '55%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '1.8s',
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.4)',
            top: '50%',
            left: '25%',
            animation: 'floatDot 15s infinite ease-in-out',
            animationDelay: '2.8s',
          }}
        ></div>
      </div>
      <div
        className='container justify-content-center align-items-center'
        style={{ padding: '20px 0', position: 'relative', zIndex: 1 }}
      >
        <div className='row'>
          <div className='col-md-8 col-lg-8' style={{ margin: '0px auto' }}>
            {/* Header Card with Sales Form Button */}
            <div
              className='card shadow'
              style={{ background: 'linear-gradient(90deg, #3b82f6, #60a5fa)' }}
            >
              <div className='card-body d-flex justify-content-between align-items-center'>
                {/* <Link
                  to='/SalesForm'
                  style={{
                    textDecoration: 'none',
                    position: 'absolute',
                    left: '15px',
                  }}
                >
                  <Button variant='light' className='btn-sales-form'>
                    Sales Form
                  </Button>
                </Link> */}
                <h4 className='card-title text-white flex-grow-1 text-center mb-0'>
                  Branch Manager Portal
                </h4>
                <Button
                  variant='outline-danger'
                  onClick={handleLogout}
                  style={{ position: 'absolute', right: '15px' }}
                >
                  <FaSignOutAlt className='me-1' /> Logout
                </Button>
              </div>
              <div className='text-center mt-3'>
                <div className='d-flex justify-content-center align-items-center'>
                  <span className='text-white p-2'>
                    Welcome{' '}
                    {userInfo.name.replace(/^\w/, (c) => c.toUpperCase())},
                  </span>
                </div>
                <div className='d-flex justify-content-center align-items-center'>
                  <span className='text-white p-2'>
                    Branches:{' '}
                    {userInfo.branches && userInfo.branches.length > 0
                      ? userInfo.branches.join(', ')
                      : 'No branches assigned'}
                  </span>
                </div>
              </div>
              <div className='text-center'>
                <div className='container'>
                  <div className='row'>
                    <div className='col-md-4'>
                      <div className='box'>
                        <h3 className='text-white'>
                          {summary.pending_transfers}
                        </h3>
                        <p className='text-white'>Pending Transfers</p>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='box'>
                        <h3 className='text-white'>
                          {summary.unassigned_orders}
                        </h3>
                        <p className='text-white'>Pending Orders</p>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='box'>
                        <h3 className='text-white'>{summary.active_drivers}</h3>
                        <p className='text-white'>Active Drivers</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className='card shadow' style={{ background: '#ffffff' }}>
              <div className='card-body'>
                <div className='container'>
                  <div className='gap-3 mb-4'>
                    <div className='row'>
                      <div style={{ width: '20%' }}>
                        <Button
                          className={`for_btn ${
                            activeForm === 'formA' ? 'active' : ''
                          }`}
                          variant='primary'
                          onClick={() => {
                            setActiveForm('formA');
                            fetchPendingOrders();
                          }}
                        >
                          <i className='fa fa-clock'></i> Pending
                        </Button>
                      </div>
                      <div style={{ width: '20%' }}>
                        <Button
                          className={`for_btn ${
                            activeForm === 'formTransfers' ? 'active' : ''
                          }`}
                          variant='info'
                          onClick={async () => {
                            setActiveForm('formTransfers');
                            // Refresh transfers data
                            try {
                              const transfersRes = await authAxios.get(
                                `/transfers?embed=order,from_driver,to_driver,from_showroom,to_showroom`
                              );
                              console.log(
                                '🔄 Raw Transfers Response:',
                                transfersRes.data
                              );
                              // Handle paginated response structure: {success: true, data: {data: [...]}}
                              let transfersData = [];
                              if (
                                transfersRes.data?.success &&
                                transfersRes.data?.data?.data
                              ) {
                                transfersData = transfersRes.data.data.data;
                              } else if (transfersRes.data?.data?.data) {
                                transfersData = transfersRes.data.data.data;
                              } else if (transfersRes.data?.data) {
                                transfersData = Array.isArray(
                                  transfersRes.data.data
                                )
                                  ? transfersRes.data.data
                                  : [];
                              } else if (Array.isArray(transfersRes.data)) {
                                transfersData = transfersRes.data;
                              }
                              console.log(
                                '🔄 Refreshed Transfers:',
                                transfersData.length,
                                transfersData
                              );
                              setTransfers(transfersData);
                              // When clicking Transfers tab, show all transfers
                              setFilteredTransfers(transfersData);
                            } catch (err) {
                              console.error(
                                '❌ Error refreshing transfers:',
                                err
                              );
                              setTransfers([]);
                            }
                          }}
                        >
                          <i className='fa fa-exchange-alt'></i> Transfers
                        </Button>
                      </div>
                      <div style={{ width: '20%' }}>
                        <Button
                          className={`for_btn ${
                            activeForm === 'formB' ? 'active' : ''
                          }`}
                          variant='success'
                          onClick={() => setActiveForm('formB')}
                        >
                          <i className='fa fa-box'></i> All Orders
                        </Button>
                      </div>
                      <div style={{ width: '20%' }}>
                        <Button
                          className={`for_btn ${
                            activeForm === 'formC' ? 'active' : ''
                          }`}
                          variant='warning'
                          onClick={() => setActiveForm('formC')}
                        >
                          <i className='fa fa-user-slash'></i> Unassigned
                        </Button>
                      </div>
                      <div style={{ width: '20%' }}>
                        <Button
                          className={`for_btn ${
                            activeForm === 'formD' ? 'active' : ''
                          }`}
                          variant='danger'
                          onClick={() => {
                            setActiveForm('formD');
                            fetchDriverSummaries();
                          }}
                        >
                          <i className='fa fa-truck'></i> Driver Load
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Pending Orders */}
                  {activeForm === 'formA' && (
                    <div
                      style={{
                        width: 'fit-content',
                        maxWidth: '100%',
                        margin: '0 auto',
                      }}
                    >
                      {pendingOrders.filter((order) => !order.is_transfer)
                        .length === 0 ? (
                        <div className='text-center py-5'>
                          <p
                            className='text-muted'
                            style={{ fontSize: '18px' }}
                          >
                            No pending orders available
                          </p>
                        </div>
                      ) : (
                        pendingOrders
                          .filter((order) => !order.is_transfer)
                          .map((order) => (
                            <Card
                              key={order.id}
                              className='mb-3'
                              style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'box-shadow 0.3s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 4px 8px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <Card.Body>
                                <div className='row'>
                                  <div className='col-md-8'>
                                    <div className='d-flex align-items-center mb-2'>
                                      <h5
                                        className='mb-0 me-2'
                                        style={{
                                          fontSize: '1.1rem',
                                          fontWeight: '600',
                                        }}
                                      >
                                        Order #{order.po_number || order.id}
                                      </h5>
                                      <Badge bg='warning'>
                                        {order.status
                                          ?.replace('_', ' ')
                                          .replace(/^\w/, (c) =>
                                            c.toUpperCase()
                                          ) || 'Pending'}
                                      </Badge>
                                    </div>
                                    <div className='mb-2'>
                                      <Badge bg='secondary' className='me-2'>
                                        {order.type
                                          ? order.type
                                              .toLowerCase()
                                              .replace(/_/g, ' ')
                                          : 'N/A'}
                                      </Badge>
                                    </div>
                                    <div className='row mt-2'>
                                      <div className='col-md-6'>
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      <div className='col-md-6'>
                                        <p
                                          className='mb-2'
                                          style={{ fontSize: '0.9rem' }}
                                        >
                                          {order?.driver ? (
                                            <>
                                              <strong>Driver:</strong>{' '}
                                              {getDriverName(order)}
                                            </>
                                          ) : null}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className='col-md-4 d-flex align-items-center justify-content-end'>
                                    <div className='d-flex flex-column gap-2'>
                                      <Button
                                        size='sm'
                                        variant='primary'
                                        onClick={() => handleShowAssign(order)}
                                        style={{ minWidth: '100px' }}
                                      >
                                        Assign Driver
                                      </Button>
                                      {order.driver_id && (
                                        <Button
                                          size='sm'
                                          variant='info'
                                          onClick={() =>
                                            handleTrackDriver(order)
                                          }
                                          style={{ minWidth: '100px' }}
                                        >
                                          <FaMapMarkerAlt className='me-1' />{' '}
                                          Track
                                        </Button>
                                      )}
                                      <Button
                                        size='sm'
                                        variant='outline-secondary'
                                        onClick={() => handleShowView(order)}
                                        style={{ minWidth: '100px' }}
                                      >
                                        View Details
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          ))
                      )}
                    </div>
                  )}

                  {/* Transfers */}
                  {activeForm === 'formTransfers' && (
                    <div
                      style={{
                        width: 'fit-content',
                        maxWidth: '100%',
                        margin: '0 auto',
                      }}
                    >
                      {transfers.length === 0 ? (
                        <div className='text-center py-5'>
                          <p
                            className='text-muted'
                            style={{ fontSize: '18px' }}
                          >
                            No transfers available
                          </p>
                        </div>
                      ) : (
                        transfers.map((transfer) => (
                          <Card
                            key={transfer.id}
                            className='mb-3'
                            style={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            <Card.Body>
                              <div className='row align-items-center'>
                                <div className='col-md-8'>
                                  <div className='d-flex align-items-center mb-2'>
                                    <h6
                                      className='mb-0 me-2'
                                      style={{
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                      }}
                                    >
                                      Transfer #{transfer.id}
                                    </h6>
                                    <Badge
                                      bg={
                                        transfer.status === 'approved'
                                          ? 'success'
                                          : transfer.status === 'rejected'
                                          ? 'danger'
                                          : transfer.status ===
                                            'pending_approval'
                                          ? 'warning'
                                          : 'secondary'
                                      }
                                      className='me-2'
                                    >
                                      {transfer.status
                                        ?.replace(/_/g, ' ')
                                        .replace(/\b\w/g, (l) =>
                                          l.toUpperCase()
                                        ) || 'Pending'}
                                    </Badge>
                                  </div>
                                  <div className='row'>
                                    <div className='col-md-6'>
                                      <p
                                        className='mb-1'
                                        style={{ fontSize: '0.9rem' }}
                                      >
                                        <strong>PO:</strong>{' '}
                                        {transfer.original_order?.po_number ||
                                          transfer.transfer_order?.po_number ||
                                          'N/A'}
                                      </p>
                                      <p
                                        className='mb-1'
                                        style={{ fontSize: '0.9rem' }}
                                      >
                                        <strong>From Driver:</strong>{' '}
                                        {transfer.from_driver?.name ||
                                          (transfer.from_driver_id
                                            ? `Driver #${transfer.from_driver_id}`
                                            : 'N/A')}
                                      </p>
                                    </div>
                                    <div className='col-md-6'>
                                      <p
                                        className='mb-1'
                                        style={{ fontSize: '0.9rem' }}
                                      >
                                        <strong>To Showroom:</strong>{' '}
                                        {transfer.to_showroom?.name ||
                                          transfer.to_showroom_id ||
                                          'N/A'}
                                      </p>
                                      <p
                                        className='mb-1'
                                        style={{ fontSize: '0.9rem' }}
                                      >
                                        <strong>Created:</strong>{' '}
                                        {formatDateTime(transfer.created_at)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className='col-md-4 text-end'>
                                  <div className='d-flex flex-column gap-2'>
                                    {transfer.status !== 'approved' &&
                                      transfer.status !== 'rejected' && (
                                        <>
                                          <Button
                                            size='sm'
                                            variant='success'
                                            onClick={() =>
                                              updateTransferStatus(
                                                transfer.id,
                                                'approved'
                                              )
                                            }
                                            style={{ minWidth: '100px' }}
                                          >
                                            <i className='fas fa-thumbs-up me-1'></i>
                                            Approve
                                          </Button>
                                          {transfer.status ===
                                            'pending_approval' && (
                                            <Button
                                              size='sm'
                                              variant='danger'
                                              onClick={() =>
                                                updateTransferStatus(
                                                  transfer.id,
                                                  'rejected'
                                                )
                                              }
                                              style={{ minWidth: '100px' }}
                                            >
                                              <i className='fas fa-thumbs-down me-1'></i>
                                              Reject
                                            </Button>
                                          )}
                                        </>
                                      )}
                                    {(transfer.status === 'approved' ||
                                      transfer.status === 'rejected') && (
                                      <Badge
                                        bg={
                                          transfer.status === 'approved'
                                            ? 'success'
                                            : 'danger'
                                        }
                                        style={{
                                          fontSize: '0.9rem',
                                          padding: '6px 12px',
                                        }}
                                      >
                                        {transfer.status === 'approved'
                                          ? '✓ Approved'
                                          : '✗ Rejected'}
                                      </Badge>
                                    )}
                                    <Button
                                      size='sm'
                                      variant='outline-secondary'
                                      onClick={() =>
                                        handleShowTransferView(transfer)
                                      }
                                      style={{ minWidth: '100px' }}
                                    >
                                      View Details
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        ))
                      )}
                    </div>
                  )}

                  {/* All Orders */}
                  {activeForm === 'formB' && (
                    <>
                      {orders.filter((order) => !order.is_transfer).length ===
                      0 ? (
                        <div className='text-center py-5'>
                          <p
                            className='text-muted'
                            style={{ fontSize: '18px' }}
                          >
                            No orders available
                          </p>
                        </div>
                      ) : (
                        orders
                          .filter(
                            (order) =>
                              !order.is_transfer &&
                              order.is_transfer !== 1 &&
                              order.is_transfer !== '1'
                          )
                          .map((order) => (
                            <Card
                              key={order.id}
                              className='mb-3'
                              style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'box-shadow 0.3s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 4px 8px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <Card.Body>
                                <div className='row'>
                                  <div className='col-md-8'>
                                    <div className='d-flex align-items-center mb-2'>
                                      <h6
                                        className='mb-0 me-2'
                                        style={{
                                          fontSize: '1rem',
                                          fontWeight: '600',
                                        }}
                                      >
                                        Order #{order.po_number || order.id}
                                      </h6>
                                      <Badge
                                        bg={
                                          order.status === 'delivered'
                                            ? 'success'
                                            : order.status === 'rejected'
                                            ? 'danger'
                                            : order.status === 'in_transit' ||
                                              order.status === 'picked_up'
                                            ? 'info'
                                            : 'warning'
                                        }
                                      >
                                        {order.status
                                          ?.replace('_', ' ')
                                          .replace(/^\w/, (c) =>
                                            c.toUpperCase()
                                          ) || 'Pending'}
                                      </Badge>
                                    </div>
                                    <div className='mb-2'>
                                      <Badge bg='secondary' className='me-2'>
                                        {order.type
                                          ? order.type
                                              .toLowerCase()
                                              .replace(/_/g, ' ')
                                          : 'N/A'}
                                      </Badge>
                                    </div>
                                    <div className='row mt-2'>
                                      <div className='col-md-6'>
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      <div className='col-md-6'>
                                        {order?.driver && (
                                          <p
                                            className='mb-2'
                                            style={{ fontSize: '0.9rem' }}
                                          >
                                            <strong>Driver:</strong>{' '}
                                            {getDriverName(order)}
                                          </p>
                                        )}
                                        {order.cod_amount && (
                                          <p
                                            className='mb-2'
                                            style={{ fontSize: '0.9rem' }}
                                          >
                                            <strong>COD Amount:</strong>{' '}
                                            {order.currency || 'AED'}{' '}
                                            {order.cod_amount}
                                          </p>
                                        )}
                                        {order.invoice_number && (
                                          <p
                                            className='mb-2'
                                            style={{ fontSize: '0.9rem' }}
                                          >
                                            <strong>Invoice:</strong>{' '}
                                            {order.invoice_number}
                                          </p>
                                        )}
                                        {order.created_at && (
                                          <p
                                            className='mb-0'
                                            style={{
                                              fontSize: '0.85rem',
                                              color: '#6c757d',
                                            }}
                                          >
                                            <strong>Created:</strong>{' '}
                                            {formatDateTime(order.created_at)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className='col-md-4 d-flex align-items-center justify-content-end'>
                                    <div className='d-flex flex-column gap-2'>
                                      {order.driver_id && (
                                        <Button
                                          size='sm'
                                          variant='info'
                                          onClick={() =>
                                            handleTrackDriver(order)
                                          }
                                          style={{ minWidth: '100px' }}
                                        >
                                          <FaMapMarkerAlt className='me-1' />{' '}
                                          Track
                                        </Button>
                                      )}
                                      <Button
                                        size='sm'
                                        variant='outline-secondary'
                                        onClick={() => handleShowView(order)}
                                        style={{ minWidth: '100px' }}
                                      >
                                        View Details
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          ))
                      )}
                    </>
                  )}

                  {/* Unassigned Orders */}
                  {activeForm === 'formC' && (
                    <div
                      style={{
                        width: 'fit-content',
                        maxWidth: '100%',
                        margin: '0 auto',
                      }}
                    >
                      {filteredOrders.filter(
                        (order) => !order.driver_id && !order.is_transfer
                      ).length === 0
                        ? null
                        : filteredOrders
                            .filter(
                              (order) => !order.driver_id && !order.is_transfer
                            )
                            .map((order) => (
                              <Card
                                key={order.id}
                                className='mb-3'
                                style={{
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '8px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                              >
                                <Card.Body>
                                  <div className='row align-items-center'>
                                    <div className='col-md-8'>
                                      <h6
                                        className='mb-2'
                                        style={{
                                          fontSize: '1rem',
                                          fontWeight: '600',
                                        }}
                                      >
                                        Order #{order.id} -{' '}
                                        {order.po_number || 'N/A'}
                                      </h6>
                                      <p
                                        className='mb-1'
                                        style={{ fontSize: '0.9rem' }}
                                      >
                                        <strong>Status:</strong>{' '}
                                        <Badge
                                          bg={
                                            order.status === 'delivered'
                                              ? 'success'
                                              : order.status === 'rejected'
                                              ? 'danger'
                                              : 'warning'
                                          }
                                        >
                                          {order.status || 'N/A'}
                                        </Badge>
                                      </p>
                                    </div>
                                    <div className='col-md-4 text-end'>
                                      <Button
                                        size='sm'
                                        variant='outline-secondary'
                                        onClick={() => handleShowView(order)}
                                      >
                                        View Details
                                      </Button>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            ))}
                    </div>
                  )}

                  {/* All Orders */}
                  {activeForm === 'formB' && (
                    <>
                      {orders.filter(
                        (order) =>
                          !order.is_transfer &&
                          order.is_transfer !== 1 &&
                          order.is_transfer !== '1'
                      ).length === 0 ? (
                        <div className='text-center py-5'>
                          <p
                            className='text-muted'
                            style={{ fontSize: '18px' }}
                          >
                            No orders available
                          </p>
                        </div>
                      ) : (
                        orders
                          .filter(
                            (order) =>
                              !order.is_transfer &&
                              order.is_transfer !== 1 &&
                              order.is_transfer !== '1'
                          )
                          .map((order) => (
                            <Card
                              key={order.id}
                              className='mb-3'
                              style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'box-shadow 0.3s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 4px 8px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <Card.Body>
                                <div className='row'>
                                  <div className='col-md-8'>
                                    <div className='d-flex align-items-center mb-2'>
                                      <h5
                                        className='mb-0 me-2'
                                        style={{
                                          fontSize: '1.1rem',
                                          fontWeight: '600',
                                        }}
                                      >
                                        Order #{order.po_number || order.id}
                                      </h5>
                                      <Badge
                                        bg={
                                          order.status === 'accepted'
                                            ? 'warning'
                                            : order.status === 'picked_up'
                                            ? 'success'
                                            : order.status === 'assigned'
                                            ? 'primary'
                                            : order.status === 'in_transit' ||
                                              order.status === 'pending'
                                            ? 'warning'
                                            : 'secondary'
                                        }
                                      >
                                        {order.status?.replace('_', ' ') ||
                                          'Pending'}
                                      </Badge>
                                    </div>
                                    <div className='mb-2'>
                                      <Badge bg='secondary' className='me-2'>
                                        {order.type
                                          ? order.type
                                              .replace(/^\w/, (c) =>
                                                c.toUpperCase()
                                              )
                                              .replace(/_/g, ' ')
                                          : 'N/A'}
                                      </Badge>
                                    </div>
                                    <div className='row mt-2'>
                                      <div className='col-md-6'>
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      <div className='col-md-6'>
                                        <p
                                          className='mb-2'
                                          style={{ fontSize: '0.9rem' }}
                                        >
                                          {order?.driver ? (
                                            <>
                                              <strong>Driver:</strong>{' '}
                                              {getDriverName(order)}
                                            </>
                                          ) : null}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className='col-md-4 d-flex align-items-center justify-content-end'>
                                    <div className='d-flex flex-column gap-2'>
                                      {!order.driver &&
                                        (order.status === 'in_transit' ||
                                          order.status === 'pending') && (
                                          <Button
                                            size='sm'
                                            variant='primary'
                                            onClick={() =>
                                              handleShowAssign(order)
                                            }
                                            style={{ minWidth: '100px' }}
                                          >
                                            Assign Driver
                                          </Button>
                                        )}
                                      {order.driver_id && (
                                        <Button
                                          size='sm'
                                          variant='info'
                                          onClick={() =>
                                            handleTrackDriver(order)
                                          }
                                          style={{ minWidth: '100px' }}
                                        >
                                          <FaMapMarkerAlt className='me-1' />{' '}
                                          Track
                                        </Button>
                                      )}
                                      <Button
                                        size='sm'
                                        variant='outline-secondary'
                                        onClick={() => handleShowView(order)}
                                        style={{ minWidth: '100px' }}
                                      >
                                        <FaEye className='me-1' /> View Details
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          ))
                      )}
                    </>
                  )}

                  {/* Unassigned Orders */}
                  {activeForm === 'formC' && (
                    <>
                      {(() => {
                        const unassignedOrdersList = unassignedOrders.filter(
                          (o) =>
                            !o.driver &&
                            !o.driver_id &&
                            !o.is_transfer &&
                            o.is_transfer !== 1 &&
                            o.is_transfer !== '1'
                        );
                        return unassignedOrdersList.length === 0 ? (
                          <div className='text-center py-5'>
                            <p
                              className='text-muted'
                              style={{ fontSize: '18px' }}
                            >
                              No unassigned pending orders available
                            </p>
                          </div>
                        ) : (
                          unassignedOrdersList.map((order) => (
                            <Card
                              key={order.id}
                              className='mb-3'
                              style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'box-shadow 0.3s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 4px 8px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow =
                                  '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <Card.Body>
                                <div className='row'>
                                  <div className='col-md-8'>
                                    <div className='d-flex align-items-center mb-2'>
                                      <h5
                                        className='mb-0 me-2'
                                        style={{
                                          fontSize: '1.1rem',
                                          fontWeight: '600',
                                        }}
                                      >
                                        Order #{order.po_number || order.id}
                                      </h5>
                                      <Badge bg='warning'>
                                        {order.status
                                          ?.replace('_', ' ')
                                          .replace(/^\w/, (c) =>
                                            c.toUpperCase()
                                          ) || 'Pending'}
                                      </Badge>
                                    </div>
                                    <div className='mb-2'>
                                      <Badge bg='secondary' className='me-2'>
                                        {order.type
                                          ? order.type
                                              .toLowerCase()
                                              .replace(/_/g, ' ')
                                          : 'N/A'}
                                      </Badge>
                                    </div>
                                    <div className='row mt-2'>
                                      <div className='col-md-6'>
                                        {order.type !== 'sales_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>From:</strong>{' '}
                                              {getVendorName(order)}
                                            </p>
                                            {order.pickup_address && (
                                              <p
                                                className='mb-2'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.pickup_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {order.type !== 'purchase_order' && (
                                          <>
                                            <p
                                              className='mb-2'
                                              style={{ fontSize: '0.9rem' }}
                                            >
                                              <strong>To:</strong>{' '}
                                              {getDestinationName(order)}
                                            </p>
                                            {order.delivery_address && (
                                              <p
                                                className='mb-0'
                                                style={{
                                                  fontSize: '0.85rem',
                                                  color: '#6c757d',
                                                }}
                                              >
                                                {order.delivery_address}
                                              </p>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      <div className='col-md-6'>
                                        <p
                                          className='mb-2'
                                          style={{ fontSize: '0.9rem' }}
                                        >
                                          <strong>Driver:</strong>{' '}
                                          <span style={{ color: '#dc3545' }}>
                                            Unassigned
                                          </span>
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className='col-md-4 d-flex align-items-center justify-content-end'>
                                    <div className='d-flex flex-column gap-2'>
                                      <Button
                                        size='sm'
                                        variant='primary'
                                        onClick={() => handleShowAssign(order)}
                                        style={{ minWidth: '100px' }}
                                      >
                                        Assign Driver
                                      </Button>
                                      {order.driver_id && (
                                        <Button
                                          size='sm'
                                          variant='info'
                                          onClick={() =>
                                            handleTrackDriver(order)
                                          }
                                          style={{ minWidth: '100px' }}
                                        >
                                          <FaMapMarkerAlt className='me-1' />{' '}
                                          Track
                                        </Button>
                                      )}
                                      <Button
                                        size='sm'
                                        variant='outline-secondary'
                                        onClick={() => handleShowView(order)}
                                        style={{ minWidth: '100px' }}
                                      >
                                        <FaEye className='me-1' /> View Details
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          ))
                        );
                      })()}
                    </>
                  )}

                  {/* Driver Load */}
                  {activeForm === 'formD' && (
                    <div
                      className='card p-4'
                      style={{
                        width: 'fit-content',
                        maxWidth: '100%',
                        margin: '0 auto',
                      }}
                    >
                      {/* Date Filters */}
                      <div className='mb-4'>
                        <Row className='align-items-end'>
                          <Col md={3}>
                            <Form.Label>From Date</Form.Label>
                            <Form.Control
                              type='date'
                              value={driverLoadDateFilter.from}
                              onChange={(e) =>
                                setDriverLoadDateFilter((prev) => ({
                                  ...prev,
                                  from: e.target.value,
                                }))
                              }
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Label>To Date</Form.Label>
                            <Form.Control
                              type='date'
                              value={driverLoadDateFilter.to}
                              onChange={(e) =>
                                setDriverLoadDateFilter((prev) => ({
                                  ...prev,
                                  to: e.target.value,
                                }))
                              }
                            />
                          </Col>
                          <Col md={3}>
                            <Button
                              variant='primary'
                              onClick={() => fetchDriverSummaries()}
                            >
                              <FaSearch className='me-1' /> Filter
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Driver</th>
                            <th>Confirmation Pending</th>
                            <th>Completed</th>
                            <th>Collections</th>
                          </tr>
                        </thead>
                        <tbody>
                          {driverSummaries.length === 0 ? (
                            <tr>
                              <td colSpan='4' className='text-center py-5'>
                                <p
                                  className='text-muted'
                                  style={{ fontSize: '18px' }}
                                >
                                  No data available
                                </p>
                              </td>
                            </tr>
                          ) : (
                            driverSummaries.map((summary) => (
                              <tr key={summary.driverId}>
                                <td>{summary.driverName}</td>
                                <td>{summary.confirmationPending || 0}</td>
                                <td>{summary.completed || 0}</td>
                                <td>{summary.collections || 0}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Assign Modal */}
            <Modal show={showAssignModal} onHide={handleCloseAssign}>
              <Modal.Header closeButton>
                <Modal.Title>
                  Assign Order #{selectedOrder?.po_number || selectedOrder?.id}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Select a driver to assign this order to:</p>
                <select
                  className='form-select'
                  value={selectedDriverId}
                  onChange={(e) => setSelectedDriverId(e.target.value)}
                >
                  <option value=''>Select Driver</option>
                  {driverOptions.length > 0 ? (
                    driverOptions.map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.name}
                      </option>
                    ))
                  ) : (
                    <option value='' disabled>
                      No drivers available for this showroom
                    </option>
                  )}
                </select>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  size='sm'
                  variant='secondary'
                  onClick={handleCloseAssign}
                >
                  Close
                </Button>
                <Button size='sm' variant='primary' onClick={assignDriver}>
                  Assign
                </Button>
              </Modal.Footer>
            </Modal>

            {/* View Modal */}
            <Modal show={showViewModal} onHide={handleCloseView} size='lg'>
              <Modal.Header closeButton>
                <Modal.Title>
                  Order #{selectedOrder?.po_number || selectedOrder?.id}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedOrder && (
                  <>
                    <Row>
                      <Col md={6}>
                        <h5>Order Information</h5>
                        <p>
                          <strong>Order ID:</strong>{' '}
                          {selectedOrder.uuid || selectedOrder.id || 'N/A'}
                        </p>
                        <p>
                          <strong>Type:</strong>
                          <Badge bg='secondary' className='ms-2'>
                            {selectedOrder.type
                              ? selectedOrder.type
                                  .replace(/^\w/, (c) => c.toUpperCase())
                                  .replace(/_/g, ' ')
                              : 'N/A'}
                          </Badge>
                        </p>
                        <p>
                          <strong>Status:</strong>
                          <Badge bg='success' className='ms-2'>
                            {selectedOrder?.status || 'Pending'}
                          </Badge>
                        </p>
                        <p>
                          <strong>Priority:</strong>{' '}
                          <Badge
                            bg={
                              selectedOrder.priority === 1
                                ? 'danger'
                                : selectedOrder.priority === 2
                                ? 'warning'
                                : 'secondary'
                            }
                          >
                            {selectedOrder.priority === 1
                              ? 'High'
                              : selectedOrder.priority === 2
                              ? 'Medium'
                              : 'Normal'}
                          </Badge>
                        </p>
                        <p>
                          <strong>PO Number:</strong>{' '}
                          {selectedOrder.po_number || 'N/A'}
                        </p>
                        <p>
                          <strong>Invoice Number:</strong>{' '}
                          {selectedOrder.invoice_number || 'N/A'}
                        </p>
                        {selectedOrder.external_source && (
                          <p>
                            <strong>External Source:</strong>{' '}
                            {selectedOrder.external_source}
                          </p>
                        )}
                        {selectedOrder.external_id && (
                          <p>
                            <strong>External ID:</strong>{' '}
                            {selectedOrder.external_id}
                          </p>
                        )}
                        {selectedOrder.company_name && (
                          <p>
                            <strong>Company Name:</strong>{' '}
                            {selectedOrder.company_name}
                          </p>
                        )}
                        <p>
                          <strong>Proof Required:</strong>{' '}
                          <Badge
                            bg={
                              selectedOrder.proof_required
                                ? 'warning'
                                : 'success'
                            }
                          >
                            {selectedOrder.proof_required ? 'Yes' : 'No'}
                          </Badge>
                        </p>
                      </Col>
                      <Col md={6}>
                        <h5>Financial Information</h5>
                        <p>
                          <strong>Payment Status:</strong>
                          <Badge
                            bg={
                              selectedOrder.payment_status === 'paid'
                                ? 'success'
                                : selectedOrder.payment_status === 'pending'
                                ? 'warning'
                                : 'secondary'
                            }
                            className='ms-2'
                          >
                            {selectedOrder.payment_status?.replace('_', ' ') ||
                              'N/A'}
                          </Badge>
                        </p>
                        <p>
                          <strong>Payment Method:</strong>{' '}
                          {selectedOrder.payment_method || 'N/A'}
                        </p>
                        <p>
                          <strong>Amount Due (COD):</strong>{' '}
                          {selectedOrder.currency || 'AED'}{' '}
                          {selectedOrder.cod_amount || '0.00'}
                        </p>
                        {selectedOrder.return_value && (
                          <p>
                            <strong>Return Value:</strong>{' '}
                            {selectedOrder.currency || 'AED'}{' '}
                            {selectedOrder.return_value}
                          </p>
                        )}
                        {selectedOrder.pending_balance &&
                          parseFloat(selectedOrder.pending_balance) > 0 && (
                            <p>
                              <strong>Pending Balance:</strong>{' '}
                              {selectedOrder.currency || 'AED'}{' '}
                              {selectedOrder.pending_balance}
                            </p>
                          )}
                      </Col>
                    </Row>

                    <Row className='mt-3'>
                      <Col md={6}>
                        <h5>Location Information</h5>
                        {selectedOrder.type !== 'sales_order' && (
                          <>
                            <p>
                              <strong>From (Vendor):</strong>{' '}
                              {getVendorName(selectedOrder)}
                            </p>
                            {selectedOrder.vendor?.phone && (
                              <p>
                                <strong>Vendor Phone:</strong>{' '}
                                {selectedOrder.vendor.phone}
                              </p>
                            )}
                            {selectedOrder.vendor?.email && (
                              <p>
                                <strong>Vendor Email:</strong>{' '}
                                {selectedOrder.vendor.email}
                              </p>
                            )}
                            <p>
                              <strong>Pickup Address:</strong>{' '}
                              {selectedOrder.pickup_address || 'N/A'}
                            </p>
                            {selectedOrder.pickup_lat &&
                              selectedOrder.pickup_lng && (
                                <p>
                                  <strong>Pickup Coordinates:</strong>{' '}
                                  {selectedOrder.pickup_lat},{' '}
                                  {selectedOrder.pickup_lng}
                                </p>
                              )}
                          </>
                        )}
                        {selectedOrder.type !== 'sales_order' &&
                          selectedOrder.type !== 'purchase_order' && <hr />}
                        {selectedOrder.type !== 'purchase_order' && (
                          <>
                            <p>
                              <strong>To:</strong>{' '}
                              {getDestinationName(selectedOrder)}
                            </p>
                            {selectedOrder.customer?.phone && (
                              <p>
                                <strong>Customer Phone:</strong>{' '}
                                {selectedOrder.customer.phone}
                              </p>
                            )}
                            {selectedOrder.customer?.email && (
                              <p>
                                <strong>Customer Email:</strong>{' '}
                                {selectedOrder.customer.email}
                              </p>
                            )}
                            {selectedOrder.showroom?.phone && (
                              <p>
                                <strong>Showroom Phone:</strong>{' '}
                                {selectedOrder.showroom.phone}
                              </p>
                            )}
                            {selectedOrder.warehouse?.name && (
                              <p>
                                <strong>Warehouse:</strong>{' '}
                                {selectedOrder.warehouse.name}
                              </p>
                            )}
                            <p>
                              <strong>Delivery Address:</strong>{' '}
                              {selectedOrder.delivery_address || 'N/A'}
                            </p>
                            {selectedOrder.delivery_lat &&
                              selectedOrder.delivery_lng && (
                                <p>
                                  <strong>Delivery Coordinates:</strong>{' '}
                                  {selectedOrder.delivery_lat},{' '}
                                  {selectedOrder.delivery_lng}
                                </p>
                              )}
                          </>
                        )}
                      </Col>
                      <Col md={6}>
                        <h5>Driver & Assignment</h5>
                        {activeForm !== 'formC' && (
                          <>
                            <p>
                              <strong>Driver:</strong>{' '}
                              {getDriverName(selectedOrder)}
                            </p>
                            {selectedOrder.driver?.phone && (
                              <p>
                                <strong>Driver Phone:</strong>{' '}
                                {selectedOrder.driver.phone}
                              </p>
                            )}
                            {selectedOrder.driver?.email && (
                              <p>
                                <strong>Driver Email:</strong>{' '}
                                {selectedOrder.driver.email}
                              </p>
                            )}
                            {selectedOrder.driver?.vehicle_no && (
                              <p>
                                <strong>Vehicle Number:</strong>{' '}
                                {selectedOrder.driver.vehicle_no}
                              </p>
                            )}
                            {selectedOrder.driver?.online !== undefined && (
                              <p>
                                <strong>Driver Status:</strong>{' '}
                                <Badge
                                  bg={
                                    selectedOrder.driver.online
                                      ? 'success'
                                      : 'secondary'
                                  }
                                >
                                  {selectedOrder.driver.online
                                    ? 'Online'
                                    : 'Offline'}
                                </Badge>
                              </p>
                            )}
                          </>
                        )}
                        {activeForm === 'formC' && (
                          <p className='text-muted'>
                            <em>Driver not assigned</em>
                          </p>
                        )}
                        <hr />
                        <h5>Important Dates</h5>
                        <p>
                          <strong>Created:</strong>{' '}
                          {formatDateTime(selectedOrder.created_at)}
                        </p>
                        <p>
                          <strong>Updated:</strong>{' '}
                          {formatDateTime(selectedOrder.updated_at)}
                        </p>
                        {selectedOrder.expected_delivery_at && (
                          <p>
                            <strong>Expected Delivery:</strong>{' '}
                            {formatDateTime(selectedOrder.expected_delivery_at)}
                          </p>
                        )}
                        {selectedOrder.delivered_at && (
                          <p>
                            <strong>Delivered:</strong>{' '}
                            {formatDateTime(selectedOrder.delivered_at)}
                          </p>
                        )}
                        {selectedOrder.returned_at && (
                          <p>
                            <strong>Returned:</strong>{' '}
                            {formatDateTime(selectedOrder.returned_at)}
                          </p>
                        )}
                        {selectedOrder.rejected_at && (
                          <p>
                            <strong>Rejected:</strong>{' '}
                            {formatDateTime(selectedOrder.rejected_at)}
                          </p>
                        )}
                      </Col>
                    </Row>

                    {(selectedOrder.remarks || selectedOrder.notes) && (
                      <Row className='mt-3'>
                        <Col md={12}>
                          <h5>Additional Information</h5>
                          {selectedOrder.remarks && (
                            <p>
                              <strong>Remarks:</strong> {selectedOrder.remarks}
                            </p>
                          )}
                          {selectedOrder.notes &&
                            selectedOrder.notes !== selectedOrder.remarks && (
                              <p>
                                <strong>Notes:</strong> {selectedOrder.notes}
                              </p>
                            )}
                        </Col>
                      </Row>
                    )}

                    <Row className='mt-4'>
                      <Col md={12}>
                        <h5>Attachments</h5>
                        {viewOrderAttachments && (
                          <>
                            <div className='d-flex flex-wrap'>
                              {viewOrderAttachments.pod_photos.map(
                                (file, idx) => (
                                  <Button
                                    key={`pod_${idx}`}
                                    variant='outline-info'
                                    size='sm'
                                    className='me-2 mb-2'
                                    onClick={() => handleOpenAttachment(file)}
                                  >
                                    <FaPaperclip /> POD {idx + 1}{' '}
                                    <FaExternalLinkAlt className='ms-1' />
                                  </Button>
                                )
                              )}

                              {viewOrderAttachments.voice_notes.map(
                                (file, idx) => (
                                  <Button
                                    key={`voice_${idx}`}
                                    variant='outline-info'
                                    size='sm'
                                    className='me-2 mb-2'
                                    onClick={() => handleOpenAttachment(file)}
                                  >
                                    <FaPaperclip /> Voice {idx + 1}{' '}
                                    <FaExternalLinkAlt className='ms-1' />
                                  </Button>
                                )
                              )}

                              {viewOrderAttachments.order_docs.map(
                                (file, idx) => (
                                  <Button
                                    key={`doc_${idx}`}
                                    variant='outline-info'
                                    size='sm'
                                    className='me-2 mb-2'
                                    onClick={() => handleOpenAttachment(file)}
                                  >
                                    <FaPaperclip /> Doc {idx + 1}{' '}
                                    <FaExternalLinkAlt className='ms-1' />
                                  </Button>
                                )
                              )}
                            </div>

                            {!viewOrderAttachments.pod_photos.length &&
                              !viewOrderAttachments.voice_notes.length &&
                              !viewOrderAttachments.order_docs.length && (
                                <p className='text-muted'>
                                  No attachments available
                                </p>
                              )}
                          </>
                        )}
                      </Col>
                    </Row>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={handleCloseView}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Order Tracking Modal */}
            <OrderTracking
              show={showTrackModal}
              handleClose={handleCloseTrackModal}
              order={trackOrder}
              driverId={trackOrder?.driver_id}
            />

            {/* Transfer View Modal */}
            <Modal
              show={showTransferViewModal}
              onHide={handleCloseTransferView}
              size='lg'
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  Transfer #{selectedTransfer?.id || 'N/A'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedTransfer && (
                  <>
                    <Row>
                      <Col md={6}>
                        <h5 style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                          Transfer Information
                        </h5>
                        <p style={{ fontSize: '0.9rem' }}>
                          <strong>Transfer ID:</strong>{' '}
                          {selectedTransfer.uuid ||
                            selectedTransfer.id ||
                            'N/A'}
                        </p>
                        <p style={{ fontSize: '0.9rem' }}>
                          <strong>Type:</strong>
                          <Badge bg='info' className='ms-2'>
                            {selectedTransfer.type
                              ? selectedTransfer.type
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())
                              : 'N/A'}
                          </Badge>
                        </p>
                        <p style={{ fontSize: '0.9rem' }}>
                          <strong>Status:</strong>
                          <Badge
                            bg={
                              selectedTransfer.status === 'approved'
                                ? 'success'
                                : selectedTransfer.status === 'rejected'
                                ? 'danger'
                                : selectedTransfer.status === 'pending_approval'
                                ? 'warning'
                                : 'secondary'
                            }
                            className='ms-2'
                          >
                            {selectedTransfer.status
                              ?.replace(/_/g, ' ')
                              .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                              'Pending'}
                          </Badge>
                        </p>
                        {selectedTransfer.remarks && (
                          <p style={{ fontSize: '0.9rem' }}>
                            <strong>Remarks:</strong> {selectedTransfer.remarks}
                          </p>
                        )}
                        <p style={{ fontSize: '0.9rem' }}>
                          <strong>Created:</strong>{' '}
                          {formatDateTime(selectedTransfer.created_at)}
                        </p>
                        <p style={{ fontSize: '0.9rem' }}>
                          <strong>Updated:</strong>{' '}
                          {formatDateTime(selectedTransfer.updated_at)}
                        </p>
                      </Col>
                      <Col md={6}>
                        <h5 style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                          Transfer Details
                        </h5>
                        {selectedTransfer.from_driver && (
                          <>
                            <p style={{ fontSize: '0.9rem' }}>
                              <strong>From Driver:</strong>{' '}
                              {selectedTransfer.from_driver.name || 'N/A'}
                            </p>
                            {selectedTransfer.from_driver.phone && (
                              <p style={{ fontSize: '0.9rem' }}>
                                <strong>Driver Phone:</strong>{' '}
                                {selectedTransfer.from_driver.phone}
                              </p>
                            )}
                            {selectedTransfer.from_driver.email && (
                              <p style={{ fontSize: '0.9rem' }}>
                                <strong>Driver Email:</strong>{' '}
                                {selectedTransfer.from_driver.email}
                              </p>
                            )}
                          </>
                        )}
                        {selectedTransfer.to_driver && (
                          <p style={{ fontSize: '0.9rem' }}>
                            <strong>To Driver:</strong>{' '}
                            {selectedTransfer.to_driver.name || 'N/A'}
                          </p>
                        )}
                        {selectedTransfer.to_showroom && (
                          <>
                            <p style={{ fontSize: '0.9rem' }}>
                              <strong>To Showroom:</strong>{' '}
                              {selectedTransfer.to_showroom.name || 'N/A'}
                            </p>
                            {selectedTransfer.to_showroom.address && (
                              <p
                                style={{
                                  fontSize: '0.85rem',
                                  color: '#6c757d',
                                }}
                              >
                                <strong>Showroom Address:</strong>{' '}
                                {selectedTransfer.to_showroom.address}
                              </p>
                            )}
                            {selectedTransfer.to_showroom.phone && (
                              <p style={{ fontSize: '0.9rem' }}>
                                <strong>Showroom Phone:</strong>{' '}
                                {selectedTransfer.to_showroom.phone}
                              </p>
                            )}
                          </>
                        )}
                        {selectedTransfer.from_showroom && (
                          <p style={{ fontSize: '0.9rem' }}>
                            <strong>From Showroom:</strong>{' '}
                            {selectedTransfer.from_showroom.name || 'N/A'}
                          </p>
                        )}
                        {selectedTransfer.requested_by && (
                          <p style={{ fontSize: '0.9rem' }}>
                            <strong>Requested By:</strong> User ID{' '}
                            {selectedTransfer.requested_by}
                          </p>
                        )}
                        {selectedTransfer.approved_by && (
                          <p style={{ fontSize: '0.9rem' }}>
                            <strong>Approved By:</strong> User ID{' '}
                            {selectedTransfer.approved_by}
                          </p>
                        )}
                      </Col>
                    </Row>

                    {/* Original Order Details */}
                    {selectedTransfer.original_order && (
                      <Row className='mt-4'>
                        <Col md={12}>
                          <h5 style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                            Original Order Details
                          </h5>
                          <div className='border p-3 rounded'>
                            <Row>
                              <Col md={6}>
                                <p style={{ fontSize: '0.9rem' }}>
                                  <strong>Order ID:</strong>{' '}
                                  {selectedTransfer.original_order.uuid ||
                                    selectedTransfer.original_order.id ||
                                    'N/A'}
                                </p>
                                <p style={{ fontSize: '0.9rem' }}>
                                  <strong>PO Number:</strong>{' '}
                                  {selectedTransfer.original_order.po_number ||
                                    'N/A'}
                                </p>
                                <p style={{ fontSize: '0.9rem' }}>
                                  <strong>Status:</strong>
                                  <Badge
                                    bg={
                                      selectedTransfer.original_order.status ===
                                      'delivered'
                                        ? 'success'
                                        : selectedTransfer.original_order
                                            .status === 'rejected'
                                        ? 'danger'
                                        : 'warning'
                                    }
                                    className='ms-2'
                                  >
                                    {selectedTransfer.original_order.status ||
                                      'N/A'}
                                  </Badge>
                                </p>
                                {selectedTransfer.original_order
                                  .pickup_address && (
                                  <p
                                    style={{
                                      fontSize: '0.85rem',
                                      color: '#6c757d',
                                    }}
                                  >
                                    <strong>Pickup Address:</strong>{' '}
                                    {
                                      selectedTransfer.original_order
                                        .pickup_address
                                    }
                                  </p>
                                )}
                              </Col>
                              <Col md={6}>
                                {selectedTransfer.original_order
                                  .delivery_address && (
                                  <p
                                    style={{
                                      fontSize: '0.85rem',
                                      color: '#6c757d',
                                    }}
                                  >
                                    <strong>Delivery Address:</strong>{' '}
                                    {
                                      selectedTransfer.original_order
                                        .delivery_address
                                    }
                                  </p>
                                )}
                                {selectedTransfer.original_order.driver && (
                                  <p style={{ fontSize: '0.9rem' }}>
                                    <strong>Driver:</strong>{' '}
                                    {selectedTransfer.original_order.driver
                                      .name || 'N/A'}
                                  </p>
                                )}
                                {selectedTransfer.original_order.cod_amount && (
                                  <p style={{ fontSize: '0.9rem' }}>
                                    <strong>COD Amount:</strong>{' '}
                                    {selectedTransfer.original_order.currency ||
                                      'AED'}{' '}
                                    {selectedTransfer.original_order.cod_amount}
                                  </p>
                                )}
                                {selectedTransfer.original_order.created_at && (
                                  <p style={{ fontSize: '0.9rem' }}>
                                    <strong>Created:</strong>{' '}
                                    {formatDateTime(
                                      selectedTransfer.original_order.created_at
                                    )}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Transfer Order Details */}
                    {selectedTransfer.transfer_order && (
                      <Row className='mt-4'>
                        <Col md={12}>
                          <h5 style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                            Transfer Order Details
                          </h5>
                          <div className='border p-3 rounded'>
                            <Row>
                              <Col md={6}>
                                <p style={{ fontSize: '0.9rem' }}>
                                  <strong>Order ID:</strong>{' '}
                                  {selectedTransfer.transfer_order.uuid ||
                                    selectedTransfer.transfer_order.id ||
                                    'N/A'}
                                </p>
                                <p style={{ fontSize: '0.9rem' }}>
                                  <strong>PO Number:</strong>{' '}
                                  {selectedTransfer.transfer_order.po_number ||
                                    'N/A'}
                                </p>
                                <p style={{ fontSize: '0.9rem' }}>
                                  <strong>Status:</strong>
                                  <Badge
                                    bg={
                                      selectedTransfer.transfer_order.status ===
                                      'delivered'
                                        ? 'success'
                                        : selectedTransfer.transfer_order
                                            .status === 'rejected'
                                        ? 'danger'
                                        : 'warning'
                                    }
                                    className='ms-2'
                                  >
                                    {selectedTransfer.transfer_order.status ||
                                      'N/A'}
                                  </Badge>
                                </p>
                                {selectedTransfer.transfer_order
                                  .pickup_address && (
                                  <p
                                    style={{
                                      fontSize: '0.85rem',
                                      color: '#6c757d',
                                    }}
                                  >
                                    <strong>Pickup Address:</strong>{' '}
                                    {
                                      selectedTransfer.transfer_order
                                        .pickup_address
                                    }
                                  </p>
                                )}
                              </Col>
                              <Col md={6}>
                                {selectedTransfer.transfer_order
                                  .delivery_address && (
                                  <p
                                    style={{
                                      fontSize: '0.85rem',
                                      color: '#6c757d',
                                    }}
                                  >
                                    <strong>Delivery Address:</strong>{' '}
                                    {
                                      selectedTransfer.transfer_order
                                        .delivery_address
                                    }
                                  </p>
                                )}
                                {selectedTransfer.transfer_order.driver && (
                                  <p style={{ fontSize: '0.9rem' }}>
                                    <strong>Driver:</strong>{' '}
                                    {selectedTransfer.transfer_order.driver
                                      .name || 'N/A'}
                                  </p>
                                )}
                                {selectedTransfer.transfer_order.cod_amount && (
                                  <p style={{ fontSize: '0.9rem' }}>
                                    <strong>COD Amount:</strong>{' '}
                                    {selectedTransfer.transfer_order.currency ||
                                      'AED'}{' '}
                                    {selectedTransfer.transfer_order.cod_amount}
                                  </p>
                                )}
                                {selectedTransfer.transfer_order.created_at && (
                                  <p style={{ fontSize: '0.9rem' }}>
                                    <strong>Created:</strong>{' '}
                                    {formatDateTime(
                                      selectedTransfer.transfer_order.created_at
                                    )}
                                  </p>
                                )}
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Status Logs */}
                    {selectedTransfer.status_logs &&
                      selectedTransfer.status_logs.length > 0 && (
                        <Row className='mt-4'>
                          <Col md={12}>
                            <h5
                              style={{ fontSize: '1.1rem', fontWeight: '600' }}
                            >
                              Status History
                            </h5>
                            <div className='border p-3 rounded'>
                              {selectedTransfer.status_logs.map((log, idx) => (
                                <div
                                  key={idx}
                                  className={
                                    idx <
                                    selectedTransfer.status_logs.length - 1
                                      ? 'mb-3 pb-3 border-bottom'
                                      : ''
                                  }
                                >
                                  <p
                                    className='mb-1'
                                    style={{ fontSize: '0.9rem' }}
                                  >
                                    <strong>From:</strong>{' '}
                                    {log.from_status?.replace(/_/g, ' ') ||
                                      'N/A'}{' '}
                                    <strong>To:</strong>{' '}
                                    {log.to_status?.replace(/_/g, ' ') || 'N/A'}
                                  </p>
                                  {log.note && (
                                    <p
                                      className='mb-1'
                                      style={{ fontSize: '0.9rem' }}
                                    >
                                      <strong>Note:</strong> {log.note}
                                    </p>
                                  )}
                                  <p
                                    className='mb-0 text-muted'
                                    style={{ fontSize: '0.85rem' }}
                                  >
                                    Changed by User ID {log.changed_by} on{' '}
                                    {formatDateTime(log.created_at)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </Col>
                        </Row>
                      )}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={handleCloseTransferView}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchManager;
